import * as React from "react";
import { FileSpreadsheet } from "lucide-react";
import {
  Reseller as ApiReseller,
  usePostV4ManufacturerByRouteManufacturerIdReportSalesAndResellerIdEmailMutation,
  usePostV4ManufacturerByRouteManufacturerIdReportSalesEmailMutation,
} from "randmar-api-client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  useToast,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { useUser } from "../../hooks/useUser";
import LegacyReseller from "../../stores/reseller/Reseller";

interface IProps {
  children?: React.ReactNode;
  trigger?: React.ReactElement;
  reseller?: LegacyReseller | ApiReseller;
  buttonTitle?: string;
  disabled?: boolean;
  triggerClassName?: string;
  triggerAriaLabel?: string;
  triggerDescribedBy?: string;
}

const isLegacy = (r?: LegacyReseller | ApiReseller): r is LegacyReseller => !!r && 'resellerId' in r;
const emailRegExp = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

const DownloadSalesReport: React.FC<IProps> = ({
  children,
  trigger,
  reseller: rawReseller,
  buttonTitle = "Sales Report",
  disabled = false,
  triggerClassName,
  triggerAriaLabel,
  triggerDescribedBy,
}) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = React.useState(false);
  const { user } = useUser();
  const [email, setEmail] = React.useState("");
  const [dateStart, setDateStart] = React.useState<string | undefined>();
  const [dateEnd, setDateEnd] = React.useState<string | undefined>();
  const { toast } = useToast();

  React.useEffect(() => {
    if (user?.profile.ApplicationName) {
      setEmail(user.profile.ApplicationName);
    }
  }, [user]);

  const [downloadResellerReport, { isLoading: isDownloadingResellerReport }] = usePostV4ManufacturerByRouteManufacturerIdReportSalesAndResellerIdEmailMutation();
  const [downloadSalesReport, { isLoading: isDownloadingSalesReport }] = usePostV4ManufacturerByRouteManufacturerIdReportSalesEmailMutation();
  const isLoading = isDownloadingResellerReport || isDownloadingSalesReport;

  const reseller: Partial<ApiReseller> = React.useMemo(
    () =>
      isLegacy(rawReseller)
        ? {
            ResellerId: rawReseller.resellerId,
            PublicName: rawReseller.contactName,
          }
        : rawReseller || {},
    [rawReseller]
  );

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && (disabled || isLoading)) {
        return;
      }
      setOpen(nextOpen);
    },
    [disabled, isLoading]
  );

  const handleConfirm = React.useCallback(async () => {
    setOpen(false);
    toast({ title: "Generating report", description: "File is being generated." });

    try {
      if (reseller?.ResellerId) {
        await downloadResellerReport({
          routeManufacturerId: applicationId,
          resellerId: reseller.ResellerId,
          emailAddress: email,
          startDate: dateStart,
          endDate: dateEnd,
        }).unwrap();
      } else {
        await downloadSalesReport({
          routeManufacturerId: applicationId,
          emailAddress: email,
          startDate: dateStart,
          endDate: dateEnd,
        }).unwrap();
      }
      toast({ variant: "success", title: "Success", description: `File will be sent to ${email}.` });
    } catch {
      toast({ variant: "destructive", title: "Error", description: `Error sending file to ${email}.` });
    }
  }, [applicationId, dateEnd, dateStart, downloadResellerReport, downloadSalesReport, email, reseller, toast]);

  const handleCancel = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleEmailChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  }, []);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {trigger ? (
            trigger
          ) : (
            <Button
              type="button"
              className={`inline-flex items-center gap-2 rounded-xl border border-[var(--ops-border)] bg-[var(--ops-card)] px-4 py-2 text-sm font-semibold text-slate-900 transition-all duration-200 hover:border-[var(--ops-border-strong)] hover:bg-[var(--ops-accent-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 data-[busy=true]:animate-pulse ${triggerClassName ?? ""}`}
              aria-label={triggerAriaLabel ?? buttonTitle}
              aria-describedby={triggerDescribedBy}
              aria-busy={isLoading}
              disabled={disabled || isLoading}
              title={buttonTitle}
            >
              <span className="flex items-center gap-2">
                {children ? children : <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />}
                <span aria-live="polite">{isLoading ? "Generating..." : buttonTitle}</span>
              </span>
            </Button>
          )}
        </DialogTrigger>

        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Download {buttonTitle}
              {reseller.PublicName &&
                <> for {reseller.PublicName}</>
              }
            </DialogTitle>
            <DialogDescription>
              Please enter your email address, so we can send you the file once generated.
              It will take about 5 minutes to generate.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid w-full items-center gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChanged}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="dateStart">Start Date</Label>
                <Input
                  type="date"
                  id="dateStart"
                  value={dateStart || ''}
                  onChange={e => setDateStart(e.target.value as string)}
                />
              </div>
              <div className="grid w-full items-center gap-1.5">
                <Label htmlFor="dateEnd">End Date</Label>
                <Input
                  type="date"
                  id="dateEnd"
                  value={dateEnd || ''}
                  onChange={e => setDateEnd(e.target.value as string)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
            <Button onClick={handleConfirm}
              disabled={isLoading || !emailRegExp.test(email) || !dateStart || !dateEnd}>
              {isLoading ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DownloadSalesReport;
